import type { StateAction } from "@entrilia/shared/types";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { requiredValidator } from "@entrilia/shared/utilities/validators";
import type {
  CreateDataImportRequest,
  DataImportInfo,
  DataImportMetadata,
  DataImportOption,
  DataImportTarget,
} from "../../../../api/types/dataImportTypes";
import type { FileInfo } from "../../../../api/types/fileTypes";
import { dataImportTargetCaptionMap } from "../../../../utilities/enumCaptions";
import type { OperationStatus } from "../importDataPagesTypes";

export interface ImportCsvDataPageState {
  currentStep: NavigationStep;
  selectedObjectType?: string;
  targetOptions: ImportCsvTargetOption[];
  importDescription: string;
  importDescriptionValidationError?: string;
  sourceFiles: ImportSourceFileInfo[];
  createImportStatus?: OperationStatus;
  importCreationError?: string;
  dataImport?: DataImportInfo;
  dataImportMetadata?: DataImportMetadata;
  applyImportStatus?: OperationStatus;
  validateImportStatus?: OperationStatus;
}

export interface NavigationStep {
  key: NavigationStepKey;
  label: string;
  previousStepKey?: NavigationStepKey;
  nextStepKey?: NavigationStepKey;
}

export type NavigationStepKey = "data" | "upload" | "review";

export interface ImportCsvTargetOption extends DataImportOption {
  key: string;
  label: string;
  selected: boolean;
  selectedFileContainerId?: string;
}

export interface ImportSourceFileInfo {
  targetOptionKey: string;
  fileInfo: FileInfo;
  status: "in_progress" | "ready" | "error";
  errorMessage?: string;
}

export const getInitialState = (preSelectedObjectType: string | undefined): ImportCsvDataPageState => {
  const initialState: ImportCsvDataPageState = {
    currentStep: navigationStepsRecord.data,
    targetOptions: [],
    importDescription: "",
    sourceFiles: [],
  };

  return preSelectedObjectType ? changeObjectTypeSelectionAction(preSelectedObjectType)(initialState) : initialState;
};

const navigationStepsList: NavigationStep[] = [
  {
    key: "data",
    label: "Data",
    nextStepKey: "upload",
  },
  {
    key: "upload",
    label: "Upload",
    previousStepKey: "data",
    nextStepKey: "review",
  },
  {
    key: "review",
    label: "Review & Finalize",
    previousStepKey: "upload",
  },
];

const navigationStepsRecord: Record<NavigationStepKey, NavigationStep> = navigationStepsList.reduce(
  (result, step) => {
    result[step.key] = step;
    return result;
  },
  {} as Record<NavigationStepKey, NavigationStep>
);

const stepKeys: NavigationStepKey[] = Object.keys(navigationStepsRecord) as NavigationStepKey[];

// Selectors

export const stepLabels: string[] = Object.values(navigationStepsRecord).map((step) => step.label);

export const getCurrentStepIndex = (state: ImportCsvDataPageState): number => stepKeys.indexOf(state.currentStep.key);

export const getObjectTypeOptions = (state: ImportCsvDataPageState): { value: string; label: string }[] =>
  distinctBy(state.targetOptions, (t) => t.objectType)
    .map(({ objectType, objectName }) => ({
      value: objectType,
      label: objectName,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const getSelectedTargetOptions = (state: ImportCsvDataPageState): ImportCsvTargetOption[] =>
  state.targetOptions.filter((t) => t.selected);

export const getSelectedDataTypeTargetsWithFiles = (
  state: ImportCsvDataPageState
): [ImportCsvTargetOption, ImportSourceFileInfo | undefined][] =>
  getSelectedTargetOptions(state).map((t) => [t, state.sourceFiles.find((f) => f.targetOptionKey === t.key)]);

export const isPreviousStepAllowed = (state: ImportCsvDataPageState): boolean =>
  state.createImportStatus === undefined && state.applyImportStatus === undefined;

export const isNextStepAllowed = (state: ImportCsvDataPageState): boolean => {
  switch (state.currentStep.key) {
    case "data": {
      return getSelectedTargetOptions(state).length > 0;
    }
    case "upload": {
      const targetOptionsWithFiles = getSelectedDataTypeTargetsWithFiles(state);

      const areAllRequiredFileContainersSelected = targetOptionsWithFiles
        .filter(([targetOption]) => targetOption.capabilities.includes("FileContainers"))
        .every(([targetOption]) => targetOption.selectedFileContainerId !== undefined);

      return (
        Boolean(state.importDescription.trim()) &&
        targetOptionsWithFiles.every(([, file]) => file?.status === "ready") &&
        areAllRequiredFileContainersSelected &&
        state.createImportStatus === undefined &&
        !state.importCreationError
      );
    }
    case "review": {
      return state.applyImportStatus === undefined;
    }
    default: {
      return false;
    }
  }
};

export const getCreateDataImportRequest = (state: ImportCsvDataPageState): CreateDataImportRequest => {
  const selectedTargetsWithFiles = getSelectedDataTypeTargetsWithFiles(state);

  return {
    descriptionUserInput: state.importDescription,
    files: selectedTargetsWithFiles
      .filter(([, sourceFile]) => sourceFile?.status === "ready")
      .map(([targetOption, sourceFile]) => ({
        fileDataCatalogueId: sourceFile?.fileInfo.catalogueId ?? "",
        fileImportTypeTarget: targetOption.target,
        fileImportObjectTypeTarget: targetOption.objectType,
        fileImportEntityTypeTarget: targetOption.bcEntityTypeCode,
        fileContainerIds: targetOption.selectedFileContainerId ? [targetOption.selectedFileContainerId] : undefined,
      })),
  };
};

export const dataImportTargetsWithSupportedExport: DataImportTarget[] = [
  "PlatformObjects",
  "PortfolioMonitoringMetrics",
  "PortfolioMonitoringMetricValues",
  "DocumentCollections",
  "Contacts",
  "ContactAccess",
  "Investors",
];

// Actions

const getTargetOptionPrefix = (importOption: DataImportOption): string =>
  importOption.bcEntityTypeName || importOption.objectName;

const getTargetOptionLabel = (importOption: DataImportOption): string =>
  `${getTargetOptionPrefix(importOption)}: ${dataImportTargetCaptionMap[importOption.target]}${importOption.canImport ? "" : " (export only)"}`;

const getImportDescription = (selectedOptions: ImportCsvTargetOption[]): string => {
  const [firstOption] = selectedOptions;
  if (!firstOption) {
    return "";
  }

  const prefix = getTargetOptionPrefix(firstOption);
  const name = selectedOptions.every((o) => getTargetOptionPrefix(o) === prefix)
    ? `${prefix}: ${selectedOptions.map((o) => dataImportTargetCaptionMap[o.target]).join(", ")}`
    : selectedOptions.map((o) => o.label).join(", ");
  return `${name} - ${formatDate(new Date())}`;
};

const createCsvTargetOption = (importOption: DataImportOption): ImportCsvTargetOption => ({
  ...importOption,
  key: `${importOption.objectType}_${importOption.target}_${importOption.bcEntityTypeCode ?? ""}`,
  label: getTargetOptionLabel(importOption),
  selected: false,
});

export const initTargetOptionsAction =
  (importOptions: DataImportOption[]): StateAction<ImportCsvDataPageState> =>
  (state) => ({
    ...state,
    targetOptions: importOptions.map(createCsvTargetOption),
  });

export const previousStepAction = (): StateAction<ImportCsvDataPageState> => (state) => {
  if (state.currentStep.previousStepKey) {
    return { ...state, currentStep: navigationStepsRecord[state.currentStep.previousStepKey] };
  }
  return state;
};

export const nextStepAction = (): StateAction<ImportCsvDataPageState> => (state) => {
  if (state.currentStep.nextStepKey) {
    return { ...state, currentStep: navigationStepsRecord[state.currentStep.nextStepKey] };
  }
  return state;
};

export const changeObjectTypeSelectionAction =
  (selectedObjectType: string): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const targetOptions = state.targetOptions.map((t) => ({ ...t, selected: false }));
    return {
      ...state,
      selectedObjectType,
      targetOptions,
    };
  };

export const changeTargetOptionSelectionAction =
  (key: string, newSelected: boolean): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const newTargetOptions = state.targetOptions.map((t) => (t.key === key ? { ...t, selected: newSelected } : t));

    return {
      ...state,
      targetOptions: newTargetOptions,
      importDescription: getImportDescription(newTargetOptions.filter((t) => t.selected)),
    };
  };

export const updateImportDescriptionAction =
  (importDescription: string): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const validationResult = requiredValidator(importDescription);

    return {
      ...state,
      importDescription,
      importDescriptionValidationError: validationResult.isValid ? undefined : validationResult.error,
    };
  };

export const deleteSourceFileAction =
  (targetOptionKey: string): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const sourceFiles = state.sourceFiles.filter((file) => file.targetOptionKey !== targetOptionKey);
    return { ...state, sourceFiles };
  };

export const startFileUploadAction =
  (targetOptionKey: string, fileName: string, fileSize: number): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const sourceFiles: ImportSourceFileInfo[] = [
      ...state.sourceFiles,
      {
        targetOptionKey,
        status: "in_progress",
        fileInfo: {
          id: "",
          catalogueId: "",
          createdBy: "",
          createdAt: "",
          fileName,
          fileSize,
        },
      },
    ];

    return { ...state, sourceFiles };
  };

export const addInvalidFileAction =
  (
    targetOptionKey: string,
    fileName: string,
    fileSize: number,
    errorMessage: string
  ): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const sourceFiles: ImportSourceFileInfo[] = [
      ...state.sourceFiles,
      {
        targetOptionKey,
        status: "error",
        errorMessage,
        fileInfo: {
          id: "",
          catalogueId: "",
          createdBy: "",
          createdAt: "",
          fileName,
          fileSize,
        },
      },
    ];

    return { ...state, sourceFiles };
  };

export const setFileUploadErrorAction =
  (targetOptionKey: string, errorMessage: string): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const sourceFiles: ImportSourceFileInfo[] = state.sourceFiles.map((file) =>
      file.targetOptionKey === targetOptionKey ? { ...file, status: "error", errorMessage } : file
    );

    return { ...state, sourceFiles };
  };

export const setFileUploadCompletedAction =
  (targetOptionKey: string, fileInfo: FileInfo): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const sourceFiles: ImportSourceFileInfo[] = state.sourceFiles.map((file) =>
      file.targetOptionKey === targetOptionKey ? { ...file, status: "ready", fileInfo } : file
    );

    return { ...state, sourceFiles };
  };

export const setSelectedFileContainerAction =
  (targetOptionKey: string, fileContainerId: string | undefined): StateAction<ImportCsvDataPageState> =>
  (state) => {
    const newTargetOptions = state.targetOptions.map((t) =>
      t.key === targetOptionKey ? { ...t, selectedFileContainerId: fileContainerId } : t
    );

    return { ...state, targetOptions: newTargetOptions };
  };

export const updateCreateImportStatusAction =
  (createImportStatus: OperationStatus): StateAction<ImportCsvDataPageState> =>
  (state) => {
    return {
      ...state,
      createImportStatus,
      importCreationError: undefined,
      dataImport: undefined,
      dataImportMetadata: undefined,
    };
  };

export const setImportCreationErrorAction =
  (errorMessage: string): StateAction<ImportCsvDataPageState> =>
  (state) => {
    return {
      ...state,
      createImportStatus: undefined,
      importCreationError: errorMessage,
      dataImport: undefined,
      dataImportMetadata: undefined,
    };
  };

export const removeImportCreationErrorAction = (): StateAction<ImportCsvDataPageState> => (state) => {
  return {
    ...state,
    createImportStatus: undefined,
    importCreationError: undefined,
    dataImport: undefined,
    dataImportMetadata: undefined,
  };
};

export const finishCreatingImportAction =
  (dataImport: DataImportInfo, dataImportMetadata: DataImportMetadata): StateAction<ImportCsvDataPageState> =>
  (state) => {
    return nextStepAction()({
      ...state,
      createImportStatus: undefined,
      importCreationError: undefined,
      dataImport,
      dataImportMetadata,
    });
  };

export const updateApplyImportStatusAction =
  (applyImportStatus: OperationStatus | undefined): StateAction<ImportCsvDataPageState> =>
  (state) => {
    return {
      ...state,
      applyImportStatus,
    };
  };

export const updateValidateImportStatusAction =
  (validateImportStatus: OperationStatus | undefined): StateAction<ImportCsvDataPageState> =>
  (state) => {
    return {
      ...state,
      validateImportStatus,
    };
  };

export const finishValidateImportAction =
  (dataImport: DataImportInfo, dataImportMetadata: DataImportMetadata): StateAction<ImportCsvDataPageState> =>
  (state) => {
    return {
      ...state,
      validateImportStatus: undefined,
      dataImport,
      dataImportMetadata,
    };
  };
